import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import BackToTop from "@/components/feature/BackToTop";
import { comics } from "@/mocks/comics";

export default function ComicDetail() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === id);
  const related = comics.filter((c) => c.id !== id).slice(0, 8);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!comic) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: comic.title,
      description: comic.desc,
      keywords: comic.tags.join(","),
      image: comic.cover,
      dateModified: new Date().toISOString(),
      author: { "@type": "Organization", name: "卡通动漫第一页" },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    script.id = "comic-ld";
    document.getElementById("comic-ld")?.remove();
    document.head.appendChild(script);
    return () => {
      document.getElementById("comic-ld")?.remove();
    };
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Seo title="未找到该漫画 - 卡通动漫第一页" description="未找到你要查看的漫画，返回卡通动漫第一页继续浏览。" />
        <Navbar />
        <main className="container-x flex min-h-[60vh] flex-col items-center justify-center gap-4 pt-16 text-center">
          <i className="ri-error-warning-line text-4xl text-foreground-300" style={{ fontSize: "42px" }}></i>
          <h1 className="font-display text-2xl text-foreground-950">没有找到这部漫画</h1>
          <p className="text-sm text-foreground-500">它可能已经下架，或者链接有误。</p>
          <Link to="/" className="rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-600 transition-colors">返回首页</Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Seo
        title={`${comic.title} - 漫画详情 | 卡通动漫第一页`}
        description={`${comic.detail} ${comic.updates}，来自卡通动漫第一页的人气连载，${comic.category}分类高分漫画。`}
        keywords={`${comic.title},${comic.category}漫画,${comic.tags.join(",")},卡通动漫第一页`}
      />
      <Navbar />

      <main className="bg-background-50 pt-16 md:pt-20">
        <div className="container-x">
          <nav className="flex flex-wrap items-center gap-2 py-6 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" style={{ fontSize: "16px" }}></i>
            <Link to="/today" className="hover:text-primary-700 transition-colors">今日更新</Link>
            <i className="ri-arrow-right-s-line" style={{ fontSize: "16px" }}></i>
            <span className="text-foreground-800">{comic.title}</span>
          </nav>

          {/* 详情头部 */}
          <section className="flex flex-col gap-6 rounded-2xl bg-background-100 p-5 ring-1 ring-background-200 md:flex-row md:p-7" aria-label="漫画详情">
            <div className="relative mx-auto aspect-[3/4] w-52 flex-shrink-0 overflow-hidden rounded-xl md:mx-0 md:w-56">
              <img src={comic.cover} alt={`${comic.title} 封面`} title={`${comic.title} 漫画`} className="h-full w-full object-cover object-top" />
            </div>
            <div className="flex flex-1 flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl text-foreground-950 md:text-3xl">{comic.title}</h1>
                <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">{comic.category}</span>
                <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs text-primary-700">{comic.status}</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {comic.tags.map((t) => (
                  <Link key={t} to={`/today?cat=${encodeURIComponent(comic.category)}`} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors">
                    #{t}
                  </Link>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div className="rounded-xl bg-background-50 p-3 ring-1 ring-background-200">
                  <div className="text-xs text-foreground-500">评分</div>
                  <div className="mt-1 flex items-center gap-1 text-lg font-medium text-primary-600"><i className="ri-star-fill" style={{ fontSize: "15px" }}></i>{comic.score}</div>
                </div>
                <div className="rounded-xl bg-background-50 p-3 ring-1 ring-background-200">
                  <div className="text-xs text-foreground-500">章节</div>
                  <div className="mt-1 text-lg font-medium text-foreground-950">{comic.chapters} 话</div>
                </div>
                <div className="rounded-xl bg-background-50 p-3 ring-1 ring-background-200">
                  <div className="text-xs text-foreground-500">人气</div>
                  <div className="mt-1 text-lg font-medium text-foreground-950">{comic.views}</div>
                </div>
                <div className="rounded-xl bg-background-50 p-3 ring-1 ring-background-200">
                  <div className="text-xs text-foreground-500">作者</div>
                  <div className="mt-1 text-lg font-medium text-foreground-950">{comic.author}</div>
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button className="w-full rounded-full bg-primary-500 px-8 py-3 text-sm font-medium whitespace-nowrap text-background-50 hover:bg-primary-600 transition-colors sm:w-auto">
                  <i className="ri-play-fill" style={{ fontSize: "15px" }}></i> 开始阅读
                </button>
                <button className="w-full rounded-full bg-accent-500 px-8 py-3 text-sm font-medium whitespace-nowrap text-foreground-950 hover:bg-accent-600 transition-colors sm:w-auto">
                  <i className="ri-add-line" style={{ fontSize: "15px" }}></i> 加入追更
                </button>
              </div>
            </div>
          </section>

          {/* 简介 */}
          <section className="mt-8" aria-label="作品简介">
            <h2 className="mb-4 flex items-center gap-2 font-display text-2xl text-foreground-950">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50"><i className="ri-information-line" style={{ fontSize: "18px" }}></i></span>
              作品简介
            </h2>
            <div className="rounded-xl bg-background-100 p-5 ring-1 ring-background-200 md:p-6">
              <p className="text-sm leading-relaxed text-foreground-600 md:text-base">{comic.detail}</p>
              <p className="mt-3 text-sm text-foreground-500">{comic.updates} · 人气 {comic.views}</p>
            </div>
          </section>

          {/* 章节 */}
          <section className="mt-10" aria-label="章节列表">
            <h2 className="mb-4 flex items-center gap-2 font-display text-2xl text-foreground-950">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-foreground-950"><i className="ri-book-2-fill" style={{ fontSize: "18px" }}></i></span>
              章节列表
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {comic.chapterList.map((ch) => (
                <button
                  key={ch.no}
                  className="group flex items-center justify-between rounded-xl bg-background-100 px-4 py-3 text-left ring-1 ring-background-200 transition-colors hover:bg-primary-50 hover:ring-primary-200"
                >
                  <span className="flex items-center gap-3 text-sm text-foreground-800">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-background-200 text-xs text-foreground-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      第{ch.no}话
                    </span>
                    <span>{ch.title}</span>
                  </span>
                  <span className="flex items-center gap-2 text-xs text-foreground-500 whitespace-nowrap">
                    {ch.updated} <i className="ri-play-circle-line text-primary-500" style={{ fontSize: "16px" }}></i>
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* 相关推荐 */}
          <section className="py-12" aria-label="相关推荐">
            <h2 className="mb-5 font-display text-2xl text-foreground-950">猜你喜欢</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}